/*
 * Planigo
 * Auteur  : Stéphane Raverdy
 * Société : SCIAD 2026
 */
import { useState, useEffect } from 'react';
import { teamsApi, servicesApi, adminApi } from '../lib/api';
import { useAuth } from '../contexts/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Avatar, AvatarFallback, AvatarImage } from '../components/ui/avatar';
import { 
  Plus, 
  Users,
  Buildings,
  UserPlus,
  Crown
} from '@phosphor-icons/react';
import { getLabel } from '../lib/utils';
import { toast } from 'sonner';

export default function TeamsPage() {
  const { isAdmin, isSupervisor, isTeamLeader } = useAuth();
  const [teams, setTeams] = useState([]);
  const [services, setServices] = useState([]);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isCreateTeamDialogOpen, setIsCreateTeamDialogOpen] = useState(false);
  const [isCreateServiceDialogOpen, setIsCreateServiceDialogOpen] = useState(false);
  const [selectedTeam, setSelectedTeam] = useState(null);
  const [availableUsers, setAvailableUsers] = useState([]);
  const [newTeam, setNewTeam] = useState({
    name: '',
    service_id: '',
    description: '',
    leader_id: '',
  });
  const [newService, setNewService] = useState({
    name: '',
    description: '',
    manager_id: '',
  });

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [teamsRes, servicesRes, usersRes] = await Promise.all([
        teamsApi.getAll(),
        servicesApi.getAll(),
        isAdmin ? adminApi.getUsers() : Promise.resolve({ data: [] }),
      ]);
      setTeams(teamsRes.data);
      setServices(servicesRes.data);
      setUsers(usersRes.data);
    } catch (error) {
      console.error('Error fetching data:', error);
      toast.error('Erreur lors du chargement');
    } finally {
      setLoading(false);
    }
  };

  const handleCreateTeam = async () => {
    try {
      await teamsApi.create(newTeam);
      toast.success('Équipe créée');
      setIsCreateTeamDialogOpen(false);
      setNewTeam({ name: '', service_id: '', description: '', leader_id: '' });
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur lors de la création');
    }
  };

  const handleCreateService = async () => {
    try {
      await servicesApi.create(newService);
      toast.success('Service créé');
      setIsCreateServiceDialogOpen(false);
      setNewService({ name: '', description: '', manager_id: '' });
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur lors de la création');
    }
  };

  const openTeam = async (team) => {
    try {
      const detail = await teamsApi.getById(team.id);
      setSelectedTeam(detail.data);
      if (isAdmin || isSupervisor || isTeamLeader) {
        try { const res = await teamsApi.getAvailableUsers(team.id); setAvailableUsers(res.data || []); }
        catch { setAvailableUsers([]); }
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Impossible d ouvrir l equipe');
    }
  };

  const refreshSelectedTeam = async (teamId) => {
    const detail = await teamsApi.getById(teamId);
    setSelectedTeam(detail.data);
    try { const res = await teamsApi.getAvailableUsers(teamId); setAvailableUsers(res.data || []); } catch { setAvailableUsers([]); }
    fetchData();
  };

  const handleAddMember = async (teamId, userId) => {
    try {
      await teamsApi.addMember(teamId, userId);
      toast.success('Membre ajouté');
      await refreshSelectedTeam(teamId);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur lors de l ajout');
    }
  };

  const handleRemoveMember = async (teamId, userId) => {
    try {
      await teamsApi.removeMember(teamId, userId);
      toast.success('Membre retiré');
      await refreshSelectedTeam(teamId);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur lors du retrait');
    }
  };

  const getInitials = (name) => {
    return name
      ?.split(' ')
      .map(n => n[0])
      .join('')
      .toUpperCase()
      .slice(0, 2) || '??';
  };

  const getServiceTeams = (serviceId) => {
    return teams.filter(t => t.service_id === serviceId);
  };

  return (
    <div className="p-6 space-y-6" data-testid="teams-page">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-heading font-bold text-slate-900">
            Équipes
          </h1>
          <p className="text-slate-600 mt-1">
            Gérez les services et équipes projet
          </p>
        </div>
        
        {isAdmin && (
          <div className="flex items-center gap-2">
            <Dialog open={isCreateServiceDialogOpen} onOpenChange={setIsCreateServiceDialogOpen}>
              <DialogTrigger asChild>
                <Button variant="outline" data-testid="create-service-btn">
                  <Buildings size={18} className="mr-2" />
                  Nouveau service
                </Button>
              </DialogTrigger>
              <DialogContent>
                <DialogHeader>
                  <DialogTitle>Créer un service</DialogTitle>
                </DialogHeader>
                <div className="space-y-4 mt-4">
                  <div className="space-y-2">
                    <Label>Nom du service *</Label>
                    <Input
                      value={newService.name}
                      onChange={(e) => setNewService(prev => ({ ...prev, name: e.target.value }))}
                      placeholder="Ex: Développement"
                      data-testid="service-name-input"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Description</Label>
                    <Textarea
                      value={newService.description}
                      onChange={(e) => setNewService(prev => ({ ...prev, description: e.target.value }))}
                      placeholder="Description du service..."
                      rows={3}
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Manager</Label>
                    <Select 
                      value={newService.manager_id} 
                      onValueChange={(value) => setNewService(prev => ({ ...prev, manager_id: value }))}
                    >
                      <SelectTrigger>
                        <SelectValue placeholder="Sélectionner un manager" />
                      </SelectTrigger>
                      <SelectContent>
                        {users.filter(u => ['admin', 'manager'].includes(u.role)).map((user) => (
                          <SelectItem key={user.id} value={user.id}>
                            {user.name}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="flex justify-end gap-3 pt-4">
                    <Button variant="outline" onClick={() => setIsCreateServiceDialogOpen(false)}>
                      Annuler
                    </Button>
                    <Button 
                      className="bg-blue-600 hover:bg-blue-700"
                      onClick={handleCreateService}
                      disabled={!newService.name}
                      data-testid="submit-service-btn"
                    >
                      Créer
                    </Button>
                  </div>
                </div>
              </DialogContent>
            </Dialog>

            <Dialog open={isCreateTeamDialogOpen} onOpenChange={setIsCreateTeamDialogOpen}>
              <DialogTrigger asChild>
                <Button className="bg-blue-600 hover:bg-blue-700" data-testid="create-team-btn">
                  <Plus size={18} className="mr-2" />
                  Nouvelle équipe
                </Button>
              </DialogTrigger>
              <DialogContent>
                <DialogHeader>
                  <DialogTitle>Créer une équipe</DialogTitle>
                </DialogHeader>
                <div className="space-y-4 mt-4">
                  <div className="space-y-2">
                    <Label>Service *</Label>
                    <Select 
                      value={newTeam.service_id} 
                      onValueChange={(value) => setNewTeam(prev => ({ ...prev, service_id: value }))}
                    >
                      <SelectTrigger data-testid="team-service-select">
                        <SelectValue placeholder="Sélectionner un service" />
                      </SelectTrigger>
                      <SelectContent>
                        {services.map((service) => (
                          <SelectItem key={service.id} value={service.id}>
                            {service.name}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-2">
                    <Label>Nom de l'équipe *</Label>
                    <Input
                      value={newTeam.name}
                      onChange={(e) => setNewTeam(prev => ({ ...prev, name: e.target.value }))}
                      placeholder="Ex: Équipe Alpha"
                      data-testid="team-name-input"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Description</Label>
                    <Textarea
                      value={newTeam.description}
                      onChange={(e) => setNewTeam(prev => ({ ...prev, description: e.target.value }))}
                      placeholder="Description de l'équipe..."
                      rows={3}
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Chef d'équipe</Label>
                    <Select 
                      value={newTeam.leader_id} 
                      onValueChange={(value) => setNewTeam(prev => ({ ...prev, leader_id: value }))}
                    >
                      <SelectTrigger>
                        <SelectValue placeholder="Sélectionner un chef d'équipe" />
                      </SelectTrigger>
                      <SelectContent>
                        {users.map((user) => (
                          <SelectItem key={user.id} value={user.id}>
                            {user.name} ({getLabel(user.role, 'role')})
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="flex justify-end gap-3 pt-4">
                    <Button variant="outline" onClick={() => setIsCreateTeamDialogOpen(false)}>
                      Annuler
                    </Button>
                    <Button 
                      className="bg-blue-600 hover:bg-blue-700"
                      onClick={handleCreateTeam}
                      disabled={!newTeam.name || !newTeam.service_id}
                      data-testid="submit-team-btn"
                    >
                      Créer
                    </Button>
                  </div>
                </div>
              </DialogContent>
            </Dialog>
          </div>
        )}
      </div>

      {/* Stats */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <Card className="card-surface">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-slate-500">Services</p>
                <p className="text-2xl font-bold text-slate-900">{services.length}</p>
              </div>
              <Buildings size={24} className="text-blue-600" />
            </div>
          </CardContent>
        </Card>
        <Card className="card-surface">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-slate-500">Équipes</p>
                <p className="text-2xl font-bold text-slate-900">{teams.length}</p>
              </div>
              <Users size={24} className="text-green-600" />
            </div>
          </CardContent>
        </Card>
        <Card className="card-surface">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-slate-500">Membres total</p>
                <p className="text-2xl font-bold text-slate-900">
                  {teams.reduce((sum, t) => sum + (t.member_count || 0), 0)}
                </p>
              </div>
              <UserPlus size={24} className="text-orange-600" />
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Services & Teams */}
      {loading ? (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {[...Array(4)].map((_, i) => (
            <div key={i} className="h-64 bg-slate-200 rounded-lg animate-pulse" />
          ))}
        </div>
      ) : services.length === 0 ? (
        <Card className="card-surface">
          <CardContent className="py-16 text-center">
            <Buildings size={48} className="mx-auto text-slate-300 mb-4" />
            <h3 className="text-lg font-medium text-slate-900 mb-2">
              Aucun service créé
            </h3>
            <p className="text-slate-500 mb-4">
              Commencez par créer un service pour organiser vos équipes
            </p>
            {isAdmin && (
              <Button 
                variant="outline"
                onClick={() => setIsCreateServiceDialogOpen(true)}
              >
                <Buildings size={18} className="mr-2" />
                Créer un service
              </Button>
            )}
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-6">
          {services.map((service) => (
            <Card key={service.id} className="card-surface" data-testid={`service-${service.id}`}>
              <CardHeader className="pb-4">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center">
                      <Buildings size={20} className="text-blue-600" />
                    </div>
                    <div>
                      <CardTitle className="text-lg">{service.name}</CardTitle>
                      <p className="text-sm text-slate-500">
                        {service.description || 'Aucune description'}
                      </p>
                    </div>
                  </div>
                  <Badge className="bg-slate-100 text-slate-700">
                    {getServiceTeams(service.id).length} équipe(s)
                  </Badge>
                </div>
              </CardHeader>
              <CardContent>
                {getServiceTeams(service.id).length === 0 ? (
                  <div className="text-center py-8 text-slate-500">
                    Aucune équipe dans ce service
                  </div>
                ) : (
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                    {getServiceTeams(service.id).map((team) => (
                      <div 
                        key={team.id}
                        className="p-4 border border-slate-200 rounded-lg hover:border-blue-300 transition-colors cursor-pointer"
                        onClick={() => openTeam(team)}
                        data-testid={`team-${team.id}`}
                      >
                        <div className="flex items-center justify-between mb-3">
                          <h4 className="font-medium text-slate-900">{team.name}</h4>
                          {team.leader_id && (
                            <Crown size={16} className="text-yellow-500" />
                          )}
                        </div>
                        <p className="text-sm text-slate-500 mb-3 line-clamp-2">
                          {team.description || 'Aucune description'}
                        </p>
                        {(team.members || []).length > 0 && (
                          <div className="mb-2 text-xs text-slate-600 line-clamp-2">
                            <span className="font-medium">Membres :</span> {(team.members || []).map(m => m.name).join(', ')}
                          </div>
                        )}
                        <div className="flex items-center justify-between">
                          <div className="flex -space-x-2">
                            {(team.members || []).slice(0, 4).map((member) => (
                              <Avatar key={member.id} className="w-7 h-7 border-2 border-white" title={member.name}>
                                {member.avatar_url && <AvatarImage src={`${process.env.REACT_APP_BACKEND_URL}${member.avatar_url}`} alt={member.name} />}
                                <AvatarFallback className="text-xs bg-slate-100">{getInitials(member.name)}</AvatarFallback>
                              </Avatar>
                            ))}
                            {(team.member_count || 0) > 4 && (
                              <div className="w-7 h-7 rounded-full bg-slate-200 border-2 border-white flex items-center justify-center">
                                <span className="text-xs text-slate-600">+{team.member_count - 4}</span>
                              </div>
                            )}
                          </div>
                          <span className="text-xs text-slate-500">
                            {team.member_count || 0} membre(s)
                          </span>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {/* Team Detail Dialog */}
      <Dialog open={!!selectedTeam} onOpenChange={() => setSelectedTeam(null)}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>{selectedTeam?.name}</DialogTitle>
          </DialogHeader>
          {selectedTeam && (
            <div className="space-y-4 mt-4">
              <p className="text-slate-600">{selectedTeam.description || 'Aucune description'}</p>
              {selectedTeam.leader && (
                <div className="flex items-center gap-3 p-3 rounded-lg border border-amber-200 bg-amber-50">
                  <Avatar className="w-9 h-9">
                    {selectedTeam.leader.avatar_url && <AvatarImage src={`${process.env.REACT_APP_BACKEND_URL}${selectedTeam.leader.avatar_url}`} alt={selectedTeam.leader.name} />}
                    <AvatarFallback>{getInitials(selectedTeam.leader.name)}</AvatarFallback>
                  </Avatar>
                  <div className="flex-1"><p className="text-xs text-amber-700">Chef d'equipe</p><p className="font-semibold text-slate-900">{selectedTeam.leader.name}</p></div>
                  <Crown size={18} className="text-amber-500" />
                </div>
              )}
              
              <div>
                <Label className="text-slate-500">Membres de l'équipe</Label>
                {selectedTeam.members?.length > 0 ? (
                  <div className="mt-2 space-y-2">
                    {selectedTeam.members.map((member) => (
                      <div key={member.id} className="flex items-center gap-3 p-2 bg-slate-50 rounded-lg">
                        <Avatar className="w-8 h-8">
                          {member.avatar_url && <AvatarImage src={`${process.env.REACT_APP_BACKEND_URL}${member.avatar_url}`} alt={member.name} />}
                          <AvatarFallback>{getInitials(member.name)}</AvatarFallback>
                        </Avatar>
                        <div className="flex-1">
                          <p className="font-medium text-slate-900">{member.name}</p>
                          <p className="text-xs text-slate-500">{member.email}</p>
                        </div>
                        <Badge className="bg-slate-100 text-slate-600">
                          {getLabel(member.role, 'role')}
                        </Badge>
                        {(isAdmin || isSupervisor || isTeamLeader) && member.id !== selectedTeam.leader_id && <Button variant="ghost" size="sm" className="text-red-600 h-7 px-2" onClick={() => handleRemoveMember(selectedTeam.id, member.id)}>Retirer</Button>}
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className="text-sm text-slate-500 mt-2">Aucun membre dans cette équipe</p>
                )}
              </div>

              {(isAdmin || isSupervisor || isTeamLeader) && (
                <div className="pt-4 border-t border-slate-200">
                  <Label>Ajouter / déplacer un membre dans cette équipe</Label>
                  <div className="flex gap-2 mt-2">
                    <Select onValueChange={(userId) => handleAddMember(selectedTeam.id, userId)}>
                      <SelectTrigger><SelectValue placeholder="Sélectionner un utilisateur" /></SelectTrigger>
                      <SelectContent>
                        {availableUsers.filter(u => !selectedTeam.members?.some(m => m.id === u.id)).map((u) => (
                          <SelectItem key={u.id} value={u.id}>{u.name}{u.team_id && u.team_id !== selectedTeam.id ? ' (autre équipe)' : ''}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <p className="text-[11px] text-slate-500 mt-1">Le rattachement équipe pilote automatiquement le périmètre du plan de charge.</p>
                </div>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
